import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useSoyuzLocales, useSoyuzFlag } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import { ZENDESK_SUPPORT } from '@spotahome/customer-graphql-modules/dist/constants';

import LazyLoginSignUpModal from '../LoginSignupModal';

import {
  traditionalSignup,
  traditionalLogin,
  traditionalForgotPassword
} from '../../api/traditional-login-client';

import {
  EXTERNAL_REDIRECTION_LOGIN_MODAL,
  COMMON_LOGIN_MODAL
} from '../utils/constants';

import createZendeskTicket from '../../mutations/zendesk';
import graphqlClient from '../../clients/graphql/landings';

const scrollTop = () => window.scrollTo({ top: 0 });

const handleSuccess = redirectUrl => {
  if (redirectUrl) {
    window.location.href = redirectUrl;
  } else {
    document.location.reload();
  }
};

const LoginSignupModalWrapper = ({
  onTraditionalLoginSuccess = handleSuccess,
  onTraditionalSignupSuccess = handleSuccess,
  deviceViewType = ''
}) => {
  const { current: locale } = useSoyuzLocales();
  const [redirectUrl, setRedirectUrl] = useState(null);
  const hideTraditionalAuth = useSoyuzFlag('HideTraditionalAuth').isOn();
  const hideFacebookOauth = useSoyuzFlag('HideFacebookOauth').isOn();
  const hideGoogleOauth = useSoyuzFlag('HideGoogleOauth').isOn();

  useEffect(() => {
    const handleExternalRedirectionOpen = ({ url, ...restPayload }) => {
      setRedirectUrl(url);
      Mediator.publish(COMMON_LOGIN_MODAL, {
        ...restPayload,
        socialOptions: {
          redirectUrl: url
        }
      });
    };

    Mediator.subscribe(
      EXTERNAL_REDIRECTION_LOGIN_MODAL,
      handleExternalRedirectionOpen
    );

    return () => {
      Mediator.unsubscribe(EXTERNAL_REDIRECTION_LOGIN_MODAL);
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(document.location.search);
    if (queryParams.get('target') === 'recover') {
      const initialEmail = queryParams.get('email');
      Mediator.publish(COMMON_LOGIN_MODAL, {
        initialAction: 'forgotten_password',
        initialValues: {
          email: initialEmail
        },
        events: {
          open: {
            eventCategory: 'Facebook recovery',
            eventAction: 'Open recover password',
            eventLabel: 'open'
          },
          close: {
            eventCategory: 'Facebook recovery',
            eventAction: 'Close recover password',
            eventLabel: 'close'
          }
        }
      });
    }
  }, []);

  const handleTraditionalLogin = async ({ email, password }) => {
    await traditionalLogin({ email, password });
    onTraditionalLoginSuccess(redirectUrl);
  };

  const handleTraditionalSignup = async ({
    name,
    email,
    password,
    marketingOptIn
  }) => {
    await traditionalSignup({
      name,
      email,
      password,
      marketingOptIn,
      locale
    });
    onTraditionalSignupSuccess(redirectUrl);
  };

  const handleHelpFormSubmit = async values => {
    const { name, email, phoneNumber, comment } = values;
    await createZendeskTicket(graphqlClient())({
      input: {
        reason: ZENDESK_SUPPORT.TENANT_REASONS.TENANT_ACCOUNT_ACCESS,
        role: 'tenant',
        name,
        email,
        phoneNumber,
        comment
      }
    });
    scrollTop();
  };

  return (
    <LazyLoginSignUpModal
      onTraditionalLogin={handleTraditionalLogin}
      onTraditionalSignup={handleTraditionalSignup}
      onForgottenPassword={traditionalForgotPassword}
      onHelpFormSubmit={handleHelpFormSubmit}
      hideTraditionalAuth={hideTraditionalAuth}
      hideFacebookOauth={hideFacebookOauth}
      hideGoogleOauth={hideGoogleOauth || deviceViewType === 'embedded-browser'}
    />
  );
};

LoginSignupModalWrapper.propTypes = {
  onTraditionalLoginSuccess: PropTypes.func,
  onTraditionalSignupSuccess: PropTypes.func,
  deviceViewType: PropTypes.string
};

export default LoginSignupModalWrapper;
